const WINDOW_STORAGE_KEY = 'aiChat.windowBounds';
const WINDOW_MARGIN = 24;
const MIN_WIDTH = 360;
const MIN_HEIGHT = 480;
const DEFAULT_WIDTH = 640;
const DEFAULT_HEIGHT = 760;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const isFiniteNumber = value => Number.isFinite(value);

const getViewportLimits = viewport => ({
    width: Math.max(1, Math.floor(viewport.width)),
    height: Math.max(1, Math.floor(viewport.height)),
});

const clampWindowBounds = (bounds, viewport) => {
    const limits = getViewportLimits(viewport);
    const width = clamp(
        Math.round(bounds.width),
        Math.min(MIN_WIDTH, limits.width),
        limits.width
    );
    const height = clamp(
        Math.round(bounds.height),
        Math.min(MIN_HEIGHT, limits.height),
        limits.height
    );

    return {
        x: clamp(Math.round(bounds.x), 0, limits.width - width),
        y: clamp(Math.round(bounds.y), 0, limits.height - height),
        width,
        height,
    };
};

const getDefaultWindowBounds = viewport => {
    const limits = getViewportLimits(viewport);
    const width = Math.min(DEFAULT_WIDTH, Math.max(1, limits.width - WINDOW_MARGIN * 2));
    const height = Math.min(DEFAULT_HEIGHT, Math.max(1, limits.height - WINDOW_MARGIN * 2));

    return clampWindowBounds({
        x: limits.width - width - WINDOW_MARGIN,
        y: limits.height - height - WINDOW_MARGIN,
        width,
        height,
    }, viewport);
};

const isValidBounds = value => (
    value && ['x', 'y', 'width', 'height'].every(key => isFiniteNumber(value[key]))
);

const readWindowBounds = (storage, viewport) => {
    try {
        const value = JSON.parse(storage && storage.getItem(WINDOW_STORAGE_KEY));
        return isValidBounds(value)
            ? clampWindowBounds(value, viewport)
            : getDefaultWindowBounds(viewport);
    } catch (error) {
        return getDefaultWindowBounds(viewport);
    }
};

const writeWindowBounds = (storage, bounds, viewport) => {
    if (!storage) return;

    try {
        storage.setItem(
            WINDOW_STORAGE_KEY,
            JSON.stringify(clampWindowBounds(bounds, viewport))
        );
    } catch (error) {
        // The window remains usable when browser storage is unavailable.
    }
};

const getPointerWindowBounds = ({ startBounds, startPoint, point, action, viewport }) => {
    const limits = getViewportLimits(viewport);
    const minimumWidth = Math.min(MIN_WIDTH, limits.width);
    const minimumHeight = Math.min(MIN_HEIGHT, limits.height);
    const start = clampWindowBounds(startBounds, viewport);
    const deltaX = point.x - startPoint.x;
    const deltaY = point.y - startPoint.y;

    if (action === 'move') {
        return clampWindowBounds({
            ...start,
            x: start.x + deltaX,
            y: start.y + deltaY,
        }, viewport);
    }

    let left = start.x;
    let top = start.y;
    let right = start.x + start.width;
    let bottom = start.y + start.height;

    if (action.includes('w')) left = clamp(left + deltaX, 0, right - minimumWidth);
    if (action.includes('e')) right = clamp(right + deltaX, left + minimumWidth, limits.width);
    if (action.includes('n')) top = clamp(top + deltaY, 0, bottom - minimumHeight);
    if (action.includes('s')) bottom = clamp(bottom + deltaY, top + minimumHeight, limits.height);

    return clampWindowBounds({
        x: left,
        y: top,
        width: right - left,
        height: bottom - top,
    }, viewport);
};

module.exports = {
    WINDOW_STORAGE_KEY,
    getDefaultWindowBounds,
    clampWindowBounds,
    readWindowBounds,
    writeWindowBounds,
    getPointerWindowBounds,
};
